import { useEffect, useRef } from "react"
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import type { StudioGuideItem } from "@/lib/types"

const OSM_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [{ id: "osm", type: "raster", source: "osm" }],
}

const DEFAULT_CENTER: [number, number] = [-9.1393, 38.7223] // Lisbon

interface MapPreviewProps {
  items: StudioGuideItem[]
  selectedItemId: number | null
  onSelectItem: (id: number) => void
  center: [number, number] | null  // [lng, lat]
}

export function MapPreview({ items, selectedItemId, onSelectItem, center }: MapPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markersRef = useRef<maplibregl.Marker[]>([])

  // Items with valid coordinates, in order
  const mappableItems = items.filter(
    (item) => item.poi?.lat != null && item.poi?.lng != null,
  )

  useEffect(() => {
    if (!containerRef.current) return

    const initialCenter: [number, number] =
      center ??
      (mappableItems[0]?.poi?.lng != null && mappableItems[0]?.poi?.lat != null
        ? [mappableItems[0].poi!.lng!, mappableItems[0].poi!.lat!]
        : DEFAULT_CENTER)

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: OSM_STYLE,
      center: initialCenter,
      zoom: 12,
    })
    map.addControl(new maplibregl.NavigationControl(), "top-right")
    mapRef.current = map

    map.on("load", () => {
      markersRef.current = mappableItems.map((item, idx) => {
        const isHighlighted = item.is_highlighted
        const isSelected = item.id === selectedItemId

        const el = document.createElement("div")
        el.style.cssText = [
          "display:flex",
          "align-items:center",
          "justify-content:center",
          `width:${isSelected ? "28px" : "22px"}`,
          `height:${isSelected ? "28px" : "22px"}`,
          "border-radius:50%",
          `background:${isHighlighted ? "#C2553A" : "#1F1A14"}`,
          "color:#fff",
          "font-size:10px",
          "font-weight:600",
          "border:2px solid #fff",
          "cursor:pointer",
          "box-shadow:0 1px 4px rgba(0,0,0,0.3)",
          isSelected ? "outline:2px solid #C2553A;outline-offset:2px" : "",
        ].join(";")
        el.textContent = String(idx + 1)
        el.title = item.poi!.name

        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([item.poi!.lng!, item.poi!.lat!])
          .addTo(map)

        el.addEventListener("click", (e) => {
          e.stopPropagation()
          onSelectItem(item.id)
        })

        return marker
      })

      // Fit bounds to all markers
      if (mappableItems.length > 1) {
        const bounds = new maplibregl.LngLatBounds()
        mappableItems.forEach((item) => bounds.extend([item.poi!.lng!, item.poi!.lat!]))
        map.fitBounds(bounds, { padding: 40, maxZoom: 14 })
      }
    })

    return () => {
      markersRef.current.forEach((m) => m.remove())
      markersRef.current = []
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Update marker styles when selectedItemId changes without rebuilding the map
  useEffect(() => {
    markersRef.current.forEach((marker, idx) => {
      const item = mappableItems[idx]
      if (!item) return
      const el = marker.getElement()
      const isSelected = item.id === selectedItemId
      const isHighlighted = item.is_highlighted
      el.style.width = isSelected ? "28px" : "22px"
      el.style.height = isSelected ? "28px" : "22px"
      el.style.background = isHighlighted ? "#C2553A" : "#1F1A14"
      el.style.outline = isSelected ? "2px solid #C2553A" : ""
      el.style.outlineOffset = isSelected ? "2px" : ""
    })
  }, [selectedItemId, mappableItems])

  return (
    <div className="w-full overflow-hidden rounded-lg" style={{ aspectRatio: "4/3" }}>
      {mappableItems.length === 0 ? (
        <div className="flex h-full w-full items-center justify-center bg-muted text-xs text-muted-foreground">
          No POIs with coordinates yet
        </div>
      ) : (
        <div ref={containerRef} className="h-full w-full" />
      )}
    </div>
  )
}
